import {
  Flex,
  OverflowText,
  Pill,
  PillWrapper,
  Text,
  TooltipWrapper
} from "@theorgchartdev/storybook";
import { FormatEmployeeDictLabel } from "@coreapp/utils/FormatEmployeeDictLabel";
import { BoxChip } from "@/Utils/BoxChip";
import { appstr } from "@coreapp/utils/appstr";
import { kOrphanID } from "@coreapp/constants";
import { EmployeeDetailIcon } from "@/appComponents/Navigation/RightSidebar/Planning/EmployeeList/EmployeeDetailIcon";
import { AssignmentStatusPill } from "@/appComponents/Blocks/Cards/ProfileBaseballCard/AssignmentStatusPill";
import { useMemo } from "react";

export const checkDisconnected = obj => {
  if (!obj) return false;
  const target = obj.IsPositionObj?.() ? obj : (obj.GetParentPosition?.() ?? obj);
  const fieldDef = target.GetFieldDefByID(kOrphanID);
  return target.GetFieldValue(fieldDef) === 1;
};

export const SearchResult = ({
  doc,
  result,
  textProps,
  doDBResult,
  isRecent = false,
  isPositionResult,
  showAssignmentStatus = false,
  assignmentStatus = null,
  mapping = null
}) => {
  const _doDBResult = doc.IsServerChart() || doDBResult;
  const _isPositionResult = isPositionResult ?? doc.IsPositionDocument();

  let chipObject = useMemo(() => {
    if (mapping) {
      return BoxChip.FromMappedObject(result, mapping, _isPositionResult);
    }
    if (_isPositionResult) {
      if (doDBResult) {
        return BoxChip.FromSearchResult(result, isRecent, doc);
      } else {
        return BoxChip.FromObjDBObject(result);
      }
    }
    return null;
  }, [result, isRecent, _isPositionResult, doDBResult, mapping, doc]);

  const GetRecordLabel = primary => {
    let labelObj;
    if (chipObject && (_isPositionResult || mapping)) {
      labelObj = {
        primary: chipObject.chipName,
        full: chipObject.label()
      };
    } else if (_doDBResult) {
      // base chart results come in as raw JSON
      const chartMeta = doc.GetIWizData();
      labelObj = FormatEmployeeDictLabel(result, chartMeta.fMapping, true);
    } else {
      // search results should be qdbobject for static custom charts
      labelObj = result.FormatEmployeeLabel(null, true);
    }
    // primary is null when the record's name field is empty (OCN5-6607); full is always a string
    const primaryText = labelObj.primary ?? "";
    if (primary) return primaryText;
    else return labelObj.full.slice(primaryText.length);
  };

  const isDisconnected = !_doDBResult ? checkDisconnected(result) : false;
  const hasChips = isDisconnected || chipObject?.isFreeHolder;
  const primaryLabel = GetRecordLabel(true);
  const secondaryLabel = GetRecordLabel(false);
  const fullLabel = `${primaryLabel ?? ""}${secondaryLabel ?? ""}`;

  return (
    <div className="oc-search-result">
      <div className="oc-search-result--content">
        {showAssignmentStatus && assignmentStatus !== null && (
          <div className="oc-search-result--icon">
            <EmployeeDetailIcon variant={assignmentStatus} />
          </div>
        )}
        <div className="oc-search-result--text">
          <Flex justifyContent={"space-between"} alignItems={"center"} gap={"8px"}>
            <div className="oc-search-result--label" style={{ minWidth: 0, flex: 1 }}>
              <OverflowText tooltipProps={{ message: fullLabel }}>
                <Text modifier={"medium"} {...textProps}>
                  {primaryLabel}
                </Text>
                <Text modifier={"lightgrey"} size={"xsmall"} {...textProps}>
                  {secondaryLabel}
                </Text>
              </OverflowText>
            </div>
            {hasChips && (
              <Flex alignItems={"center"} gap={"4px"} flex={"0 0 auto"}>
                {isDisconnected && (
                  <PillWrapper>
                    <TooltipWrapper
                      tooltipProps={{
                        message: appstr("Record is disconnected from hierarchy")
                      }}
                    >
                      <Pill label={appstr("Disconnected")} color={"warning"} />
                    </TooltipWrapper>
                  </PillWrapper>
                )}
                {chipObject?.isFreeHolder && (
                  <AssignmentStatusPill
                    doc={doc}
                    record={result}
                    dataObj={null}
                    showLabel={false}
                  />
                )}
              </Flex>
            )}
          </Flex>
        </div>
      </div>
    </div>
  );
};
